<script setup>
import useNodeProps from '@/Composables/useNodeProps'
import useSelectCategoriesModal from '@/Composables/useSelectCategories'
import Grid from '@/Components/Inputs/BlockEditor/Extension/Components/Grid.vue'
import EditGrid from '@/Components/Inputs/BlockEditor/Extension/Modals/EditGrid.vue'
import CategoryCard from '@/Components/Inputs/BlockEditor/Extension/Components/Categories/CategoryCardPreview.vue'
import EditableNodeWrapper from '@/Components/Inputs/BlockEditor/Extension/Components/EditableNodeWrapper.vue'
import { onMounted, ref, watch } from 'vue'
import { PrimaryButton } from '@codinglabsau/ui'
import { RectangleStackIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { NodeViewContent, nodeViewProps, NodeViewWrapper } from '@tiptap/vue-3'

const props = defineProps({
  ...nodeViewProps,
})

let {
  cols,
  spacing,
} = useNodeProps(props)

let selectedCategories = ref([])

onMounted(() => {
  if (props.extension.options.data.hasOwnProperty(props.node.attrs.id)) {
    selectedCategories.value = [...props.extension.options.data[props.node.attrs.id].categories]
  }
})

const addCategory = (category) => {
  if (selectedCategories.value.find(obj => obj.id === category.id)) {
    alert(__('Category already added'))
    return
  }
  selectedCategories.value.push(category)
}

const { open } = useSelectCategoriesModal(addCategory)

const removeCategory = (id) => {
  selectedCategories.value = selectedCategories.value.filter((category) => category.id !== id)
}

watch(() => selectedCategories.value, () => {
  props.updateAttributes({
    categories: selectedCategories.value.map(category => category.id),
  })
}, { deep: true })

</script>

<template>
  <node-view-wrapper>
    <EditableNodeWrapper v-bind="props">
      <template #tools>
        <EditGrid
          v-model:cols="cols"
          v-model:spacing="spacing"
        />
      </template>
      <template #content>
        <div class="w-full">
          <div class="flex w-full">
            <node-view-content class="first:prose-headings:mt-2" />
          </div>
          <div v-if="selectedCategories.length > 0">
            <Grid :cols="cols" :spacing="spacing">
              <CategoryCard
                v-for="category in selectedCategories"
                @remove="removeCategory(category.id)"
                :category="category"
              />
              <div
                @click.prevent="open"
                class="relative cursor-pointer rounded-xl bg-white border-2 border-gray-300 border-dashed text-gray-300 hover:bg-gray-300 hover:bg-opacity-50 hover:text-white"
              >
                <div class="self-stretch flex-grow">
                  <div class="aspect-1">
                    <div class="absolute inset-0 flex items-center justify-center">
                      <div class="text-center">
                        Add a new Category
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Grid>
          </div>

          <div v-else class="my-4 text-center space-y-1">
            <RectangleStackIcon class="mx-auto w-12 h-12 text-gray-300" />
            <h3 class="text-sm font-semibold text-gray-600">
              {{ __('No Categories selected') }}
            </h3>
            <p class="text-gray-400 text-sm">
              {{ __('Select or search a Category') }}
            </p>
            <PrimaryButton
              @click.prevent="open"
              type="button"
            >
              <PlusIcon class="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              {{ __('Select Category') }}
            </PrimaryButton>
          </div>
        </div>
      </template>
    </EditableNodeWrapper>
  </node-view-wrapper>
</template>
